import { useAnalytics } from '@/components/molecules/AnalyticsProvider';
import { AnalyticsEvent } from '@/lib/handleActionTracking';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import React, { PropsWithChildren, type JSX } from 'react';

export enum ButtonVariants {
  Primary = 'Primary',
  Secondary = 'Secondary',
  Tertiary = 'Tertiary',
  Quaternary = 'Quaternary',
  Destructive = 'Destructive',
  SSO = 'SSO',
}

export interface PositionedHeroIconProps {
  icon: JSX.Element;
  position?: 'left' | 'right' | 'top' | 'bottom';
  className?: string;
}
export interface BaseButtonProps
  extends Pick<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'onClick' | 'disabled' | 'type' | 'onMouseEnter' | 'onMouseLeave'
  > {
  icon?: PositionedHeroIconProps;
  analyticsEvent?: AnalyticsEvent;
  'aria-label': string;
  background?: string;
  shadow?: string;
  theme?: 'light' | 'dark';
  size?: 'small' | 'default' | 'large';
}

export interface ButtonProps extends BaseButtonProps {
  variant?: ButtonVariants;
  loading?: boolean;
  size?: 'small' | 'default' | 'large';
  theme?: 'light' | 'dark';
}

const Button: React.FC<PropsWithChildren<ButtonProps>> = ({
  variant = ButtonVariants.Primary,
  loading = false,
  children,
  icon,
  theme = 'light',
  onClick = () => {},
  analyticsEvent,
  size = 'default',
  background,
  shadow,
  ...props
}) => {
  const { handleActionTracking } = useAnalytics();

  let sizeClasses = 'text-body1Medium py-[13px] h-[48px]'; // default
  if (size === 'small') {
    sizeClasses = 'text-body2Medium py-s h-[36px]';
  } else if (size === 'large') {
    sizeClasses = 'text-h4Medium py-[16px] h-[60px]';
  }

  let wrapperClassName = `group flex flex-grow w-full relative border-box inline-flex items-center justify-center cursor-default rounded-full 
    ${background || ''}
    ${sizeClasses} 
    ${icon ? 'px-l' : 'px-xl'} 
    ${shadow || ''}
    `;

  switch (variant) {
    case ButtonVariants.SSO:
      wrapperClassName = `${wrapperClassName} border border-neutral-300`;
      if (props.disabled || loading) {
        wrapperClassName = `${wrapperClassName} text-neutral-600 bg-neutral-200`;
      } else {
        wrapperClassName = `${wrapperClassName} text-neutral-900 bg-white cursor-pointer hover:bg-neutral-100`;
      }
      break;
    case ButtonVariants.Secondary:
      wrapperClassName = `${wrapperClassName} border`;
      if (theme === 'light') {
        if (props.disabled || loading) {
          wrapperClassName = `${wrapperClassName} text-neutral-600 border-neutral-600 opacity-[0.4]`;
        } else {
          wrapperClassName = `${wrapperClassName} text-brandMidnight hover:text-brandMidnight-hover border-brandMidnight hover:border-brandMidnight-hover cursor-pointer`;
        }
      } else if (props.disabled || loading) {
        wrapperClassName = `${wrapperClassName} text-neutral-600 border-neutral-600 opacity-[0.4]`;
      } else {
        wrapperClassName = `${wrapperClassName} text-white border-white cursor-pointer hover:text-brandMidnight-hover hover:border-brandElectric-hover hover:bg-brandElectric-hover`;
      }
      break;

    case ButtonVariants.Tertiary:
      wrapperClassName = `${wrapperClassName} border`;
      if (props.disabled || loading) {
        wrapperClassName = `${wrapperClassName} text-neutral-600 bg-neutral-400`;
      } else {
        wrapperClassName = `${wrapperClassName} text-brandMidnight hover:text-brandMidnight-hover bg-brandElectric border-brandElectric hover:bg-brandElectric-hover hover:border-brandElectric-hover cursor-pointer`;
      }
      break;
    case ButtonVariants.Quaternary:
      wrapperClassName = `${wrapperClassName} border`;
      if (props.disabled || loading) {
        wrapperClassName = `${wrapperClassName} text-neutral-600 bg-neutral-200`;
      } else {
        wrapperClassName = `${wrapperClassName} text-neutral-900 bg-white border-white opacity-80 hover:shadow-lg hover:opacity-100 cursor-pointer`;
      }
      break;
    case ButtonVariants.Destructive:
      if (props.disabled) {
        wrapperClassName = `${wrapperClassName} text-neutral-600 bg-red-light`;
      } else if (loading) {
        wrapperClassName = `${wrapperClassName} text-red-medium bg-red-light`;
      } else {
        wrapperClassName = `${wrapperClassName} text-neutral-0 bg-red-medium cursor-pointer`;
      }
      break;
    default: // ButtonVariants.Primary
      if (theme === 'dark') {
        if (props.disabled) {
          wrapperClassName = `${wrapperClassName} border border-neutral-600 text-neutral-600 bg-transparent`;
        } else if (loading) {
          wrapperClassName = `${wrapperClassName} border border-brandElectric text-neutral-400 bg-transparent`;
        } else {
          wrapperClassName = `${wrapperClassName} text-neutral-0 bg-neutral-800/80 hover:bg-neutral-900 cursor-pointer`;
        }
      } else if (props.disabled || loading) {
        wrapperClassName = `${wrapperClassName} text-neutral-600 bg-neutral-400`;
      } else {
        wrapperClassName = `${wrapperClassName} text-neutral-0 bg-brandMidnight hover:bg-brandMidnight-hover cursor-pointer border-brandMidnight hover:border-brandMidnight-hover border`;
      }
      break;
  }

  return (
    <button
      className={wrapperClassName}
      onClick={(e) => {
        if (analyticsEvent) {
          handleActionTracking(analyticsEvent);
        }
        onClick(e);
      }}
      {...props}
    >
      <div
        className={`inline-flex items-center justify-center gap-s whitespace-nowrap`}
      >
        {icon && icon.position === 'left' && icon.icon}
        {children}
        {icon && icon.position !== 'left' && icon.icon}

        {loading && (
          <div className="absolute animate-spin">
            <ArrowPathIcon
              className={clsx(
                `size-xl`,
                theme === 'dark' ? 'text-brandElectric' : 'text-brandMidnight'
              )}
            />
          </div>
        )}
      </div>
    </button>
  );
};

export default Button;
